// StartHereStrip — a compact, always-available "Start here" rail rendered near
// the top of the home page (WP3, §5b). It re-offers the three guided journeys
// plus the 2-minute orientation so a returning SME can pick up onboarding they
// skipped from the first-run welcome. Each item dispatches the same
// prime:start-tour event the welcome uses; completed tours show a check. The
// strip retires itself once all four tours are done or the user dismisses it.
import { useState } from 'react';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useOnboardingState } from '../../lib/onboardingState.js';
import { journeyCards, orientation } from '../../content/welcomeContent.js';
import { startTour } from './tourEvents.js';
import { readStartHereDismissed, setStartHereDismissed } from './startHereDismissal.js';
import './firstRunWelcome.css';

const ALL_ITEMS = [orientation, ...journeyCards];
const ALL_TOUR_IDS = ALL_ITEMS.map((item) => item.tourId);

export function StartHereStrip() {
  const { user } = useCurrentUser();
  const userId = user?.id;
  const { toursCompleted } = useOnboardingState(userId);
  // Local dismissed state seeded from the self-owned preference store so the
  // strip disappears immediately on click and stays gone across reloads.
  const [dismissed, setDismissed] = useState(() => readStartHereDismissed(userId));

  const completed = new Set(toursCompleted || []);
  const allDone = ALL_TOUR_IDS.every((id) => completed.has(id));
  if (dismissed || allDone) return null;

  const dismiss = () => {
    setStartHereDismissed(userId);
    setDismissed(true);
  };

  return (
    <section className="start-here-strip" data-testid="start-here-strip" aria-label="Start here">
      <header className="start-here-head">
        <div>
          <div className="eyebrow">Start here</div>
          <p className="start-here-sub">Short guided walkthroughs, tailored to your role.</p>
        </div>
        <button
          type="button"
          className="start-here-close"
          data-testid="start-here-dismiss"
          onClick={dismiss}
          aria-label="Hide the Start here strip"
        >
          <span aria-hidden>×</span>
        </button>
      </header>
      <div className="start-here-cards">
        {ALL_ITEMS.map((item) => {
          const done = completed.has(item.tourId);
          return (
            <button
              key={item.tourId}
              type="button"
              className={`start-here-card${item.tourId === 'orientation' ? ' is-orientation' : ''}${
                done ? ' is-done' : ''
              }`}
              data-testid={`start-here-card-${item.tourId}`}
              onClick={() => startTour(item.tourId)}
            >
              <span className="start-here-card-top">
                <span className="start-here-minutes">{item.minutes} min</span>
                {done ? (
                  <span
                    className="start-here-done"
                    data-testid={`start-here-done-${item.tourId}`}
                    aria-label="Completed"
                  >
                    ✓
                  </span>
                ) : null}
              </span>
              <span className="start-here-card-title">{item.title}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
